import React from "react";
import {
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonMenuToggle,
  IonPage,
  IonRow,
  IonText,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { menu } from "ionicons/icons";
import Seo from "../../common/Seo";
import { PaymentBrands } from "../../components/PaymentBrands/PaymentBrands";
import { SELLER_CARD, SELLER_COD, SHOP_EMAIL, SHOP_PHONE } from "../../common/sellers";

/*
 * "Uslovi kupovine i plaćanja": the statements Banca Intesa checks before approving card payments
 * (EPM rules 2.1.1 - 2.1.10). The standard paragraphs are the bank's own template texts.
 * Every "DOPUNITI" marker must be filled in before card payment is switched on.
 */

const SHOP = "BojenjePoBrojevima.rs";

const Section: React.FC<{ title: string }> = ({ title, children }) => (
  <IonCard>
    <IonCardHeader className="b f15" style={{ color: "var(--ion-color-dark)" }}>
      {title}
    </IonCardHeader>
    <IonCardContent>
      <IonText>
        <div className="ion-text-justify" style={{ lineHeight: 1.5 }}>
          {children}
        </div>
      </IonText>
    </IonCardContent>
  </IonCard>
);

const Todo: React.FC = ({ children }) => (
  <span style={{ background: "#fff3cd", color: "#8a6d3b", padding: "0 4px" }}>
    [DOPUNITI: {children}]
  </span>
);

const Doc: React.FC<{ file: string }> = ({ file, children }) => (
  <a href={`/assets/docs/${file}`} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

const Terms = () => (
  <IonPage>
    <Seo
      title="Uslovi kupovine i plaćanja"
      description="Uslovi kupovine, plaćanja, dostave, reklamacija i povraćaja sredstava na sajtu BojenjePoBrojevima.rs."
      path="/uslovi"
    />
    <IonHeader>
      <IonToolbar color="primary">
        <IonButtons slot="start">
          <IonMenuToggle>
            <IonIcon icon={menu} style={{ fontSize: 28 }} />
          </IonMenuToggle>
        </IonButtons>
        <IonTitle>Uslovi kupovine i plaćanja</IonTitle>
      </IonToolbar>
    </IonHeader>
    <IonContent>
      <IonGrid>
        <IonRow>
          <IonCol size="12" sizeMd="10" offsetMd="1" sizeLg="8" offsetLg="2">
            <Section title="Podaci o prodavcu">
              Prodavac zavisi od načina plaćanja koji izaberete u korpi i naveden je pre
              potvrde porudžbine, u potvrdi porudžbine i na računu:
              <ul style={{ paddingLeft: 20 }}>
                <li>
                  porudžbine koje plaćate <b>pouzećem</b> prodaje <b>{SELLER_COD.name}</b>;
                </li>
                <li>
                  porudžbine koje plaćate <b>platnom karticom</b> prodaje{" "}
                  <b>{SELLER_CARD.name}</b>.
                </li>
              </ul>
              {[SELLER_COD, SELLER_CARD].map((s) => (
                <p key={s.pib} style={{ margin: "0 0 12px" }}>
                  <b>{s.fullName}</b>
                  <br />
                  Adresa: {s.address}
                  <br />
                  Matični broj: {s.mb}
                  <br />
                  PIB: {s.pib}
                  <br />
                  Delatnost i šifra delatnosti: {s.activity}
                </p>
              ))}
              Web adresa: https://bojenjepobrojevima.rs
              <br />
              Telefon: {SHOP_PHONE}
              <br />
              Email: {SHOP_EMAIL}
            </Section>

            <Section title="Proizvodi i cene">
              Na sajtu {SHOP} prodajemo setove za bojenje po brojevima i dodatnu opremu
              (blind ramove, štafelaje, setove boja). Uz svaki proizvod navedeni su opis,
              dimenzije i cena u dinarima (RSD).
              <br />
              <br />
              {SELLER_COD.vatStatement} {SELLER_CARD.vatStatement}
            </Section>

            <Section title="Izjava o konverziji">
              Sva plaćanja biće izvršena u lokalnoj valuti Republike Srbije – dinar (RSD). Za
              informativni prikaz cena u drugim valutama koristi se srednji kurs Narodne Banke
              Srbije. Iznos za koji će biti zadužena Vaša platna kartica biće izražen u Vašoj
              lokalnoj valuti kroz konverziju u istu po kursu koji koriste kartičarske
              organizacije, a koji nama u trenutku transakcije ne može biti poznat. Kao rezultat
              ove konverzije postoji mogućnost neznatne razlike od originalne cene navedene na
              našem sajtu.
              <br />
              <br />
              <i>
                All payments will be effected in Serbian currency – dinar (RSD). The amount your
                credit card account will be charged for is obtained through the conversion of the
                price in Euro into Serbian dinar according to the current exchange rate of the
                Serbian National Bank. When charging your credit card, the same amount is
                converted into your local currency according to the exchange rate of credit card
                associations. As a result of this conversion there is a possibility of a slight
                difference from the original price stated in our web site.
              </i>
            </Section>

            <Section title="Načini plaćanja">
              Porudžbinu možete platiti pouzećem (kuriru, prilikom preuzimanja paketa) ili
              platnom karticom (Visa, Mastercard, Maestro, DinaCard, American Express) preko
              zaštićene stranice banke.
              <br />
              <br />
              Kod plaćanja karticom iznos porudžbine se prilikom naručivanja rezerviše na računu
              Vaše kartice, a naplaćuje se kada paket bude predat kurirskoj službi. Ako porudžbina
              ne bude poslata, rezervacija se poništava i sredstva se oslobađaju. Motivi koji nisu
              na stanju mogu se rezervisati; oni se ne naplaćuju unapred, već Vas kontaktiramo kada
              stignu.
              <br />
              <br />
              Pre plaćanja dobijate specifikaciju porudžbine sa cenama, a nakon plaćanja potvrdu
              o plaćanju na sajtu i na email adresu koju ste uneli.
            </Section>

            <Section title="Zaštita poverljivih podataka o transakciji">
              Prilikom unošenja podataka o platnoj kartici, poverljive informacije se prenose
              putem javne mreže u zaštićenoj (kriptovanoj) formi upotrebom SSL protokola i PKI
              sistema, kao trenutno najsavremenije kriptografske tehnologije.
              <br />
              <br />
              Sigurnost podataka prilikom kupovine garantuje procesor platnih kartica, Banca
              Intesa ad Beograd, pa se tako kompletni proces naplate obavlja na stranicama banke.
              Niti jednog trenutka podaci o platnoj kartici nisu dostupni našem sistemu.
            </Section>

            <Section title="Dostava">
              Isporuku vrši kurirska služba na adresu koju navedete u porudžbini. Proizvodi koji
              su na stanju stižu u roku od 2–4 radna dana u Srbiji, odnosno 3–6 radnih dana u
              Bosni i Hercegovini. Cena dostave prikazana je u korpi pre potvrde porudžbine.
              <br />
              <br />
              Za isporuku u Bosnu i Hercegovinu cene se informativno prikazuju u KM, a plaćanje
              karticom se vrši u dinarima (videti izjavu o konverziji). Na pošiljke za Bosnu i
              Hercegovinu primenjuju se carinski propisi BiH.
              <Todo>proveriti rokove i eventualna ograničenja dostave</Todo>
            </Section>

            <Section title="Reklamacije i odustajanje od kupovine">
              <b>Rok za podnošenje reklamacije.</b> Reklamaciju zbog nesaobraznosti robe
              (oštećenje, nedostatak, pogrešno isporučen proizvod) možete izjaviti u roku od{" "}
              <b>2 godine od dana prijema robe</b>, putem{" "}
              <Doc file="Reklamacioni-list.pdf">reklamacionog lista</Doc>, na email {SHOP_EMAIL}{" "}
              ili poštom na adresu prodavca. Uz reklamaciju dostavite račun ili drugi dokaz o
              kupovini (npr. broj porudžbine). Prijem reklamacije potvrđujemo pisanim ili
              elektronskim putem, uz broj pod kojim je reklamacija zavedena.
              <br />
              <br />
              <b>Rok za odgovor i rešavanje reklamacije.</b> Bez odlaganja, a najkasnije u roku od{" "}
              <b>8 dana od prijema reklamacije</b>, pisanim ili elektronskim putem odgovaramo da
              li je prihvatamo i na koji način ćemo je rešiti (popravka, zamena, umanjenje cene ili
              povraćaj novca). Reklamaciju rešavamo najkasnije u roku od{" "}
              <b>15 dana od dana podnošenja</b>. Ako iz objektivnih razloga nije moguće rešiti je u
              tom roku, rok se može produžiti samo jednom, o čemu Vas obaveštavamo pre isteka roka,
              uz navođenje novog roka. Troškove vraćanja robe zbog opravdane reklamacije snosi
              prodavac.
              <br />
              <br />
              <b>Odustanak od kupovine.</b> Imate pravo da odustanete od ugovora zaključenog na
              daljinu u roku od <b>14 dana od prijema robe</b>, bez navođenja razloga, slanjem
              obrasca ili izjave o odustanku na email {SHOP_EMAIL}. Robu vraćate najkasnije 14
              dana od slanja izjave; direktne troškove vraćanja robe snosi kupac. Novac vraćamo
              bez odlaganja, a najkasnije u roku od <b>14 dana od prijema izjave o odustanku</b>;
              ako ste platili karticom, povraćaj se vrši isključivo na istu platnu karticu.
              Detalji su u dokumentima{" "}
              <Doc file="Pravo-na-odustajanje.pdf">Pravo na odustajanje</Doc> i{" "}
              <Doc file="Pravilnik-o-kataloškom-poručivanju-proizvoda.pdf">
                Pravilnik o kataloškom poručivanju proizvoda
              </Doc>
              .
              <br />
              <br />
              Odustanak i reklamaciju rešava prodavac Vaše porudžbine: {SELLER_COD.name} za
              porudžbine plaćene pouzećem, a {SELLER_CARD.name} za porudžbine plaćene karticom.
            </Section>

            <Section title="Povraćaj sredstava">
              U slučaju vraćanja robe i povraćaja sredstava kupcu koji je prethodno platio nekom
              od platnih kartica, delimično ili u celosti, a bez obzira na razlog vraćanja,{" "}
              {SELLER_CARD.name} je u obavezi da povraćaj vrši isključivo preko VISA, EC/MC, Maestro, Amex i Dina
              metoda plaćanja, što znači da će banka na zahtev prodavca obaviti povraćaj
              sredstava na račun korisnika kartice.
            </Section>

            <Section title="Zaštita privatnosti korisnika">
              U ime {SHOP} obavezujemo se da ćemo čuvati privatnost svih naših kupaca. Prikupljamo
              samo neophodne, osnovne podatke o kupcima/korisnicima i podatke neophodne za
              poslovanje i informisanje korisnika u skladu sa dobrim poslovnim običajima i u cilju
              pružanja kvalitetne usluge. Dajemo kupcima mogućnost izbora uključujući mogućnost
              odluke da li žele ili ne da se izbrišu sa mailing lista koje se koriste za
              marketinške kampanje. Svi podaci o korisnicima/kupcima se strogo čuvaju i dostupni
              su samo zaposlenima kojima su ti podaci nužni za obavljanje posla. Svi zaposleni{" "}
              {SHOP} (i poslovni partneri) odgovorni su za poštovanje načela zaštite privatnosti.
              <br />
              <br />
              Više u dokumentima{" "}
              <Doc file="Obavestenje-o-politici-privatnosti.pdf">
                Obaveštenje o politici privatnosti
              </Doc>{" "}
              i{" "}
              <Doc file="Pravilnik-o-zaštiti-podataka-ličnosti.pdf">
                Pravilnik o zaštiti podataka ličnosti
              </Doc>
              .
            </Section>

            <Section title="Kontakt i korisnički servis">
              Za pitanja o porudžbini, reklamacije ili otkaz porudžbine obratite nam se na email{" "}
              {SHOP_EMAIL}, telefon {SHOP_PHONE} ili na adresu prodavca Vaše porudžbine:{" "}
              {SELLER_COD.name}, {SELLER_COD.address} (plaćanje pouzećem), odnosno{" "}
              {SELLER_CARD.name}, {SELLER_CARD.address} (plaćanje karticom).
            </Section>

            <PaymentBrands />
          </IonCol>
        </IonRow>
      </IonGrid>
    </IonContent>
  </IonPage>
);

export default Terms;
